import React, { useMemo, useState, useEffect } from "react";
import { useAppStore } from "../../../../../context/store";
import { Button, CloseButton, Dialog, HStack, Input, Portal, VStack } from "@chakra-ui/react";
import { LuMinus, LuPlus } from "react-icons/lu";
import { cloneEntity, createUniqueId, getUsernameAndUserIdFormat, matchKeyInText, removeEpochSuffix, removeFileExtension } from "../../../../../utilies/Utility";
import { Cartesian3, Color, ColorMaterialProperty, GeoJsonDataSource, JulianDate, Matrix4, PolylineDashMaterialProperty, PropertyBag, Transforms } from "cesium";

import Type from "../../../../../utilies/Types";
import { useMapObjects } from "../../../../../context/MapObjectsContext";

const LayersEditPropertiesPopup = ({ openProperties, geojson }) => {
  // state
  const { mapObjects, setMapObjects } = useMapObjects();
  const currentLayerEditing = useAppStore((s) => s.currentLayerEditing);
  const basicGeojsonStyle = useAppStore((s) => s.basicGeojsonStyle);
  const userData = useAppStore((s) => s.userData);

  // setters
  const setCursorMode = useAppStore.getState().setCursorMode;
  const pushUndo = useAppStore.getState().pushUndo;
  const setSavedPropsInNewEntity = useAppStore.getState().setSavedPropsInNewEntity;
  const setSavedPropsInNewEntityLayerKey = useAppStore.getState().setSavedPropsInNewEntityLayerKey;

  const [open, setOpen] = openProperties;
  const [properties, setProperties] = useState([]);
  const [targetLayerId, setTargetLayerId] = useState("");

  const targetOptions = useMemo(() => {
    const t = currentLayerEditing?.geojson_type;
    return (mapObjects || []).filter((o) => o?.geojson_type === t);
  }, [mapObjects, currentLayerEditing]);

  const targetLayer = useMemo(() => {
    return (mapObjects || []).find((o) => String(o.id) === String(targetLayerId)) || null;
  }, [mapObjects, targetLayerId]);

  function getLayerKey(layer) {
    const v = layer?.id ?? layer?.layer_id ?? layer?.layerName ?? layer?.layer_name ?? layer?.name ?? "";
    return String(v || "");
  }

  function getColumnKeys(layer) {
    return Array.isArray(layer?.geojson_table_columns) ? layer.geojson_table_columns.map((c) => c?.column_name).filter((v) => typeof v === "string" && v.length > 0) : [];
  }

  // true only if key is exactly "id" (case-insensitive)
  function isExactIdKey(key) {
    if (typeof key !== "string") return false;
    return key.toLowerCase() === "id";
  }

  // NEW: normalize shorthand values for specific keys
  function normalizeShorthandValueForKey(key, value, isDeletion) {
    if (isDeletion) return value;

    const k = String(key || "")
      .trim()
      .toLowerCase();
    const v = String(value ?? "")
      .trim()
      .toLowerCase();

    if (!k) return value;

    if (k === "subtype") {
      if (v === "d") return "dashed";
      if (v === "s") return "solid";
      return value;
    }

    if (k === "color") {
      if (v === "w") return "white";
      if (v === "y") return "yellow";
      return value;
    }

    return value;
  }

  /**
   * getUniquePropertyValues
   * - Collect unique values for a given property key across all features in a GeoJSON FeatureCollection.
   */
  const getUniquePropertyValues = (gjson, key) => {
    if (!gjson || !gjson.features || !Array.isArray(gjson.features)) return [];
    const uniqueValues = new Set();
    gjson.features.forEach((feature) => {
      const value = feature?.properties?.[key];
      if (value !== undefined && value !== null) uniqueValues.add(value);
    });
    return Array.from(uniqueValues);
  };

  function collectIdsFromGeoJSON(gjson, out = new Set()) {
    if (!gjson?.features || !Array.isArray(gjson.features)) return out;

    for (const f of gjson.features) {
      const n = Number(f?.properties?.id);
      if (Number.isFinite(n)) out.add(n);
    }

    return out;
  }

  function getMaxIdFromGeoJSON(gjson) {
    const ids = collectIdsFromGeoJSON(gjson);
    let max = -Infinity;

    for (const id of ids) {
      if (id > max) max = id;
    }

    return max === -Infinity ? null : max;
  }

  function collectIdsFromDataSource(ds, out = new Set()) {
    if (!ds?.entities) return out;
    const now = JulianDate.now();
    const list = ds.entities.values || [];

    for (const e of list) {
      const v = e.properties?.getValue?.(now) || {};
      const n = Number(v.id);
      if (Number.isFinite(n)) out.add(n);
    }

    return out;
  }

  function getMaxIdFromDataSource(ds) {
    const ids = collectIdsFromDataSource(ds);
    let max = -Infinity;

    for (const id of ids) {
      if (id > max) max = id;
    }

    return max === -Infinity ? null : max;
  }

  function getStoredMaxIdFromLayer(layer) {
    const candidates = [layer?.bboxMaxFeaturePropertiesId, layer?.bboxMaxId];

    for (const candidate of candidates) {
      const value = Number(candidate);
      if (Number.isFinite(value)) return value;
    }

    return null;
  }

  function getFallbackMaxIdForLayer(layer) {
    let max = -Infinity;

    const lastId = Number(layer?.geojson_last_id);
    if (Number.isFinite(lastId)) max = Math.max(max, lastId);

    for (const gjson of [layer?.geojson, layer?.fullGeojson, layer?.hiddenGeojson, layer?.original_geojson]) {
      const gjsonMax = getMaxIdFromGeoJSON(gjson);
      if (Number.isFinite(gjsonMax)) max = Math.max(max, gjsonMax);
    }

    const dsMax = getMaxIdFromDataSource(layer?.dataSource);
    if (Number.isFinite(dsMax)) max = Math.max(max, dsMax);

    return Number.isFinite(max) ? max : 0;
  }

  function buildUsedFeatureIdSet(layer) {
    const ids = new Set();

    for (const gjson of [layer?.geojson, layer?.fullGeojson, layer?.hiddenGeojson, layer?.original_geojson]) {
      collectIdsFromGeoJSON(gjson, ids);
    }

    collectIdsFromDataSource(layer?.dataSource, ids);

    return ids;
  }

  function getNextIdForLayer(layer) {
    const storedMax = getStoredMaxIdFromLayer(layer);
    const base = Number.isFinite(storedMax) ? storedMax : getFallbackMaxIdForLayer(layer);
    const usedIds = buildUsedFeatureIdSet(layer);
    let next = base + 2;

    while (usedIds.has(next)) {
      next += 2;
    }

    return next;
  }

  function commitAllocatedIdForLayer(layer, nextId) {
    if (!layer || !Number.isFinite(nextId)) return;

    layer.geojson_last_id = nextId;
    layer.bboxMaxId = nextId;
    layer.bboxMaxFeaturePropertiesId = nextId;

    setMapObjects((prev) =>
      prev.map((o) =>
        String(o?.id) === String(layer.id) || String(o?.layer_id) === String(layer.layer_id)
          ? {
              ...o,
              geojson_last_id: nextId,
              bboxMaxId: nextId,
              bboxMaxFeaturePropertiesId: nextId,
            }
          : o,
      ),
    );
  }

  function allocateNextIdForLayer(layer) {
    const nextId = getNextIdForLayer(layer);
    commitAllocatedIdForLayer(layer, nextId);
    return nextId;
  }

  // AUTO-CUT for straight lane_marker / virtual_line when crossing cut_line
  function isLaneMarkerOrVirtualLine(layer) {
    const fn = String(layer?.file_name || "").toLowerCase();
    return fn.includes("lane_marker") || fn.includes("virtual_line");
  }

  function isCutLineDataSource(ds) {
    const ln = String(ds?.layerName || "").toLowerCase();
    return ln.includes("cut_line");
  }

  function getPolylinePositions(entity) {
    const posProp = entity?.polyline?.positions;
    if (!posProp) return null;
    try {
      if (Array.isArray(posProp)) return posProp.slice();
      if (typeof posProp.getValue === "function") {
        const v = posProp.getValue(JulianDate.now());
        return Array.isArray(v) ? v.slice() : v || null;
      }
      return null;
    } catch {
      return null;
    }
  }

  function maxTurnAngleDeg(worldPositions) {
    if (!Array.isArray(worldPositions) || worldPositions.length < 3) return 0;
    const origin = worldPositions[0];
    const enuToEcef = Transforms.eastNorthUpToFixedFrame(origin);
    const ecefToEnu = Matrix4.inverse(enuToEcef, new Matrix4());
    const pts = worldPositions.map((p) => Matrix4.multiplyByPoint(ecefToEnu, p, new Cartesian3()));

    let maxDeg = 0;
    for (let i = 1; i < pts.length - 1; i++) {
      const a = pts[i - 1];
      const b = pts[i];
      const c = pts[i + 1];

      const v1x = b.x - a.x;
      const v1y = b.y - a.y;
      const v2x = c.x - b.x;
      const v2y = c.y - b.y;

      const n1 = Math.hypot(v1x, v1y);
      const n2 = Math.hypot(v2x, v2y);
      if (n1 === 0 || n2 === 0) continue;

      const dot = (v1x * v2x + v1y * v2y) / (n1 * n2);
      const clamped = Math.max(-1, Math.min(1, dot));
      const ang = Math.acos(clamped) * (180 / Math.PI);
      if (ang > maxDeg) maxDeg = ang;
    }
    return maxDeg;
  }

  function segmentIntersection2D(p1, p2, q1, q2) {
    const rX = p2.x - p1.x;
    const rY = p2.y - p1.y;
    const sX = q2.x - q1.x;
    const sY = q2.y - q1.y;

    const denom = rX * sY - rY * sX;
    if (Math.abs(denom) < 1e-9) return null;

    const qpX = q1.x - p1.x;
    const qpY = q1.y - p1.y;

    const t = (qpX * sY - qpY * sX) / denom;
    const u = (qpX * rY - qpY * rX) / denom;

    if (t < 0 || t > 1 || u < 0 || u > 1) return null;

    const z = p1.z + t * (p2.z - p1.z);
    return { t, point: new Cartesian3(p1.x + t * rX, p1.y + t * rY, z) };
  }

  function collectCutLineEntities(allLayers) {
    const out = [];
    for (const layer of allLayers || []) {
      const ds = layer?.dataSource;
      if (!ds || !isCutLineDataSource(ds)) continue;
      for (const e of ds.entities?.values || []) {
        if (e?.polyline) out.push(e);
      }
    }
    return out;
  }

  function splitPolylineByCutLines(worldPositions, cutLineEntities) {
    if (!Array.isArray(worldPositions) || worldPositions.length < 2) return null;

    const origin = worldPositions[0];
    const enuToEcef = Transforms.eastNorthUpToFixedFrame(origin);
    const ecefToEnu = Matrix4.inverse(enuToEcef, new Matrix4());

    const toENU = (p) => Matrix4.multiplyByPoint(ecefToEnu, p, new Cartesian3());
    const toECEF = (p) => Matrix4.multiplyByPoint(enuToEcef, p, new Cartesian3());

    const baseENU = worldPositions.map(toENU);

    const cutsPerSeg = Array.from({ length: baseENU.length - 1 }, () => []);

    for (const cutEnt of cutLineEntities || []) {
      const cutPosWorld = getPolylinePositions(cutEnt);
      if (!Array.isArray(cutPosWorld) || cutPosWorld.length < 2) continue;
      const cutENU = cutPosWorld.map(toENU);

      for (let i = 0; i < baseENU.length - 1; i++) {
        const p1 = baseENU[i];
        const p2 = baseENU[i + 1];

        for (let j = 0; j < cutENU.length - 1; j++) {
          const q1 = cutENU[j];
          const q2 = cutENU[j + 1];

          const hit = segmentIntersection2D(p1, p2, q1, q2);
          if (!hit) continue;

          cutsPerSeg[i].push({ t: hit.t, enuPoint: hit.point });
        }
      }
    }

    // Deduplicate and sort cuts per segment
    const EPS_M = 0.02;
    for (let i = 0; i < cutsPerSeg.length; i++) {
      cutsPerSeg[i].sort((a, b) => a.t - b.t);
      const dedup = [];
      for (const c of cutsPerSeg[i]) {
        const last = dedup[dedup.length - 1];
        if (!last) {
          dedup.push(c);
          continue;
        }
        const dx = c.enuPoint.x - last.enuPoint.x;
        const dy = c.enuPoint.y - last.enuPoint.y;
        if (Math.hypot(dx, dy) > EPS_M) dedup.push(c);
      }
      cutsPerSeg[i] = dedup;
    }

    const expandedENU = [baseENU[0]];
    const cutIndices = [];

    for (let i = 0; i < cutsPerSeg.length; i++) {
      for (const c of cutsPerSeg[i]) {
        expandedENU.push(c.enuPoint);
        cutIndices.push(expandedENU.length - 1);
      }
      expandedENU.push(baseENU[i + 1]);
    }

    if (!cutIndices.length) return null;

    // Build segment arrays (ECEF), sharing cut points
    const segments = [];
    let startIdx = 0;
    for (const cutIdx of cutIndices) {
      const segENU = expandedENU.slice(startIdx, cutIdx + 1);
      if (segENU.length >= 2) segments.push(segENU.map(toECEF));
      startIdx = cutIdx;
    }
    const lastENU = expandedENU.slice(startIdx);
    if (lastENU.length >= 2) segments.push(lastENU.map(toECEF));

    return segments.length >= 2 ? segments : null;
  }

  // Default dropdown to currentLayerEditing when popup opens
  useEffect(() => {
    if (!open) return;
    const defaultId = currentLayerEditing?.id != null ? String(currentLayerEditing.id) : "";
    setTargetLayerId(defaultId);
    setProperties([]);
  }, [open, currentLayerEditing]);

  useEffect(() => {
    if (!open) return;
    if (!targetLayer) {
      setProperties([]);
      return;
    }

    // Use LAST entity properties (from target DataSource) as template
    const ents = Array.isArray(targetLayer?.dataSource?.entities?.values) ? targetLayer.dataSource.entities.values : [];
    const lastIndex = ents.length > 0 ? ents.length - 1 : -1;
    const templateProps = lastIndex >= 0 ? (ents[lastIndex]?.properties?.getValue?.(JulianDate.now()) ?? {}) : {};
    const originalKeys = Object.keys(templateProps);

    const columnKeys = Array.isArray(targetLayer?.geojson_table_columns) ? targetLayer.geojson_table_columns.map((c) => c?.column_name).filter((v) => typeof v === "string" && v.length > 0) : [];

    const seen = new Set();
    const allKeys = [];

    for (const k of originalKeys) {
      if (!seen.has(k)) {
        seen.add(k);
        allKeys.push(k);
      }
    }
    for (const k of columnKeys) {
      if (!seen.has(k)) {
        seen.add(k);
        allKeys.push(k);
      }
    }

    const hasId = allKeys.some((k) => isExactIdKey(k));
    if (!hasId) allKeys.unshift("id");

    const nextId = getNextIdForLayer(targetLayer);

    const rows = allKeys.map((key) => {
      const isExactId = isExactIdKey(key);
      const dataList = isExactId ? [] : getUniquePropertyValues(targetLayer?.geojson, key);

      let defaultValue = "";
      if (
        String(key || "")
          .trim()
          .toLowerCase() === "lane_id"
      )
        defaultValue = "";
      else if (isExactId) defaultValue = nextId;
      else if (templateProps[key] !== undefined && templateProps[key] !== null) defaultValue = templateProps[key];
      else defaultValue = "";

      return {
        index: createUniqueId(),
        key,
        value: defaultValue,
        data: dataList,
      };
    });

    setProperties(rows);
  }, [open, targetLayer, currentLayerEditing]);

  // Style copy keys
  const STYLE_PROPS = {
    point: ["pixelSize", "color", "outlineColor", "outlineWidth", "scaleByDistance", "translucencyByDistance", "distanceDisplayCondition", "heightReference", "disableDepthTestDistance"],
    polyline: ["material", "width", "clampToGround", "classificationType", "zIndex", "distanceDisplayCondition", "shadows", "depthFailMaterial", "arcType", "disableDepthTestDistance"],
    polygon: ["material", "outline", "outlineColor", "outlineWidth", "classificationType", "zIndex", "shadows", "stRotation", "distanceDisplayCondition", "disableDepthTestDistance"],
  };

  function copyProps(src, dst, keys) {
    if (!src || !dst) return;
    for (const k of keys) {
      if (src[k] !== undefined) dst[k] = src[k];
    }
  }

  function safeCssColor(css, fallback = Color.WHITE) {
    try {
      if (typeof css === "string" && css.trim()) return Color.fromCssColorString(css);
    } catch (_) {}
    return fallback;
  }

  function normalizeTagColor(value) {
    if (!value) return null;
    const v = String(value).trim().toLowerCase();
    if (!v || v === "unknown" || v === "none" || v === "null") return null;
    return String(value).trim();
  }

  function normalizeSubtype(value) {
    if (!value) return null;
    const v = String(value).trim().toLowerCase();
    if (!["solid", "dashed", "dotted", "pattern"].includes(v)) return null;
    return v;
  }

  function isLaneMarkerCurrentLayer() {
    const s = `${currentLayerEditing?.file_name || ""} ${currentLayerEditing?.name || ""} ${currentLayerEditing?.layerName || ""} ${currentLayerEditing?.layer_name || ""}`.toLowerCase();
    return /lane[\s_-]*marker/.test(s);
  }

  function buildGeojsonOption() {
    let geojsonOption = {};
    for (const key in basicGeojsonStyle) {
      if (matchKeyInText(key, removeEpochSuffix(removeFileExtension(currentLayerEditing?.file_name)) || "")) {
        geojsonOption = basicGeojsonStyle[key];
        break;
      }
    }
    if (Object.keys(geojsonOption).length === 0) {
      geojsonOption = {
        dashPattern: 255,
        defaultStyle: "Solid",
        solidDashLength: 0,
        defaultFillColor: "#dc2de2ff",
        defaultLineWidth: 3,
        defaultOutlineColor: "#dc2de2ff",
        dashedDashLength: 16,
        dottedDashLength: "11110000",
      };
    }
    return geojsonOption;
  }

  function applyFallbackStyle(targetEntity, geojsonOption) {
    const tags = targetEntity?.properties?.getValue?.(JulianDate.now()) || {};

    const styleFillCss = normalizeTagColor(tags.color) || geojsonOption.defaultFillColor;
    const styleOutlineCss = normalizeTagColor(tags.color) || tags.outlineColor || geojsonOption.defaultOutlineColor;

    const width = Number.isFinite(Number(tags.strokeWidth)) ? Number(tags.strokeWidth) : Number.isFinite(Number(geojsonOption.defaultLineWidth)) ? Number(geojsonOption.defaultLineWidth) : 3;

    let styleType = String(geojsonOption.defaultStyle || "solid").toLowerCase();
    if (typeof tags?.subtype === "string") {
      const st = tags.subtype.toLowerCase();
      if (["solid", "dotted", "dashed", "pattern"].includes(st)) styleType = st;
    }

    if (targetEntity.point) {
      const c = safeCssColor(styleOutlineCss, Color.WHITE);
      targetEntity.point.pixelSize = width || 6;
      targetEntity.point.color = c;
      targetEntity.point.outlineColor = c;
      targetEntity.point.outlineWidth = 1;
      targetEntity.point.disableDepthTestDistance = Number.POSITIVE_INFINITY;
    }

    if (targetEntity.polyline) {
      const c = safeCssColor(styleOutlineCss, Color.YELLOW);

      if (styleType === "dashed") {
        targetEntity.polyline.material = new PolylineDashMaterialProperty({
          color: c,
          dashLength: geojsonOption.dashedDashLength ?? 16,
        });
      } else if (styleType === "dotted") {
        targetEntity.polyline.material = new PolylineDashMaterialProperty({
          color: c,
          dashLength: 5,
        });
      } else if (styleType === "pattern") {
        const dashPattern =
          typeof geojsonOption.dottedDashLength === "string"
            ? parseInt(geojsonOption.dottedDashLength, 2)
            : Number.isFinite(Number(geojsonOption.dashPattern))
              ? Number(geojsonOption.dashPattern)
              : 255;

        targetEntity.polyline.material = new PolylineDashMaterialProperty({
          color: c,
          dashLength: 12,
          dashPattern,
        });
      } else {
        targetEntity.polyline.material = new ColorMaterialProperty(c);
      }

      targetEntity.polyline.width = width;
      targetEntity.polyline.disableDepthTestDistance = Number.POSITIVE_INFINITY;
    }

    if (targetEntity.polygon) {
      const fill = safeCssColor(styleFillCss, Color.fromBytes(0, 153, 255, 160));
      const outline = safeCssColor(styleOutlineCss, Color.BLACK);

      targetEntity.polygon.material = fill;
      targetEntity.polygon.outline = true;
      targetEntity.polygon.outlineColor = outline;
      targetEntity.polygon.disableDepthTestDistance = Number.POSITIVE_INFINITY;
    }
  }

  // Override only color/subtype if provided
  function applyTagOverrides(targetEntity, geojsonOption) {
    if (!isLaneMarkerCurrentLayer()) return;
    const tags = targetEntity?.properties?.getValue?.(JulianDate.now()) || {};
    const tagColor = normalizeTagColor(tags.color);
    const subtype = normalizeSubtype(tags.subtype);

    const hasColor = !!tagColor;
    const hasSubtype = !!subtype;
    if (!hasColor && !hasSubtype) return;

    const colorForUse = hasColor ? safeCssColor(tagColor, Color.WHITE) : null;

    if (targetEntity.point) {
      if (hasColor) {
        targetEntity.point.color = colorForUse;
        targetEntity.point.outlineColor = colorForUse;
      }
    }

    if (targetEntity.polyline) {
      const lineColor = hasColor ? colorForUse : Color.YELLOW;

      if (hasSubtype) {
        if (subtype === "dashed") {
          targetEntity.polyline.material = new PolylineDashMaterialProperty({
            color: lineColor,
            dashLength: geojsonOption.dashedDashLength ?? 16,
          });
        } else if (subtype === "dotted") {
          targetEntity.polyline.material = new PolylineDashMaterialProperty({
            color: lineColor,
            dashLength: 5,
          });
        } else if (subtype === "pattern") {
          const dashPattern =
            typeof geojsonOption.dottedDashLength === "string"
              ? parseInt(geojsonOption.dottedDashLength, 2)
              : Number.isFinite(Number(geojsonOption.dashPattern))
                ? Number(geojsonOption.dashPattern)
                : 255;

          targetEntity.polyline.material = new PolylineDashMaterialProperty({
            color: lineColor,
            dashLength: 12,
            dashPattern,
          });
        } else {
          targetEntity.polyline.material = new ColorMaterialProperty(lineColor);
        }
      } else if (hasColor) {
        const m = targetEntity.polyline.material;
        if (m && typeof m === "object" && "color" in m) {
          try {
            m.color = lineColor;
          } catch (_) {
            targetEntity.polyline.material = new ColorMaterialProperty(lineColor);
          }
        } else {
          targetEntity.polyline.material = new ColorMaterialProperty(lineColor);
        }
      }
    }

    if (targetEntity.polygon) {
      if (hasColor) {
        targetEntity.polygon.material = colorForUse;
        targetEntity.polygon.outline = true;
        targetEntity.polygon.outlineColor = colorForUse;
      }
    }
  }

  function applyTemplateStyle(targetEntity, templateEntity) {
    const geojsonOption = buildGeojsonOption();

    // Apply base style
    if (!templateEntity) {
      applyFallbackStyle(targetEntity, geojsonOption);
      applyTagOverrides(targetEntity, geojsonOption);
      return;
    }

    if (templateEntity.point && targetEntity.point) {
      copyProps(templateEntity.point, targetEntity.point, STYLE_PROPS.point);
    }
    if (templateEntity.polyline && targetEntity.polyline) {
      copyProps(templateEntity.polyline, targetEntity.polyline, STYLE_PROPS.polyline);
    }
    if (templateEntity.polygon && targetEntity.polygon) {
      copyProps(templateEntity.polygon, targetEntity.polygon, STYLE_PROPS.polygon);
    }

    // Override color/subtype if present
    applyTagOverrides(targetEntity, geojsonOption);
  }

  const add = () => {
    setCursorMode(Type.CURSOR.DEFAULT);

    const propsObj = {};
    for (const row of properties) {
      if (row?.key && String(row.key).trim() !== "") {
        propsObj[row.key] = row.value;
      }
    }
    propsObj["creator"] = getUsernameAndUserIdFormat(userData);

    if (!targetLayer?.dataSource) return;

    // Template entity (existing behavior)
    const templateEntity = targetLayer?.dataSource?.entities?.values?.[0] || null;

    if (geojson?.__type === "PARALLEL_MULTI") {
      const selected = Array.isArray(geojson.selectedEntities) ? geojson.selectedEntities : [];
      if (!selected.length) {
        setOpen(false);
        return;
      }

      const getPositions = (entity) => {
        if (!entity?.polyline?.positions) return null;
        const posProp = entity.polyline.positions;
        if (Array.isArray(posProp)) return posProp.slice();
        if (typeof posProp.getValue === "function") {
          const val = posProp.getValue(JulianDate.now());
          return Array.isArray(val) ? val.slice() : val || null;
        }
        return null;
      };

      const cloneStyle = (polyline) => {
        const t = JulianDate.now();
        const width = typeof polyline.width?.getValue === "function" ? polyline.width.getValue(t) : (polyline.width ?? 2.0);
        const material = polyline.material ?? Color.YELLOW;
        const clampToGround = typeof polyline.clampToGround?.getValue === "function" ? !!polyline.clampToGround.getValue(t) : !!polyline.clampToGround;
        const arcType = polyline.arcType;
        const zIndex = typeof polyline.zIndex?.getValue === "function" ? polyline.zIndex.getValue(t) : (polyline.zIndex ?? 0);
        return { width, material, clampToGround, arcType, zIndex };
      };

      const offsetPolylineENU = (worldPositions, signedMeters) => {
        const origin = worldPositions[0];
        const enuToEcef = Transforms.eastNorthUpToFixedFrame(origin);
        const ecefToEnu = Matrix4.inverse(enuToEcef, new Matrix4());

        const enuPts = worldPositions.map((p) => Matrix4.multiplyByPoint(ecefToEnu, p, new Cartesian3()));

        const n = enuPts.length;
        const out = new Array(n);
        for (let i = 0; i < n; i++) {
          let dx = 0,
            dy = 0;
          if (i > 0) {
            dx += enuPts[i].x - enuPts[i - 1].x;
            dy += enuPts[i].y - enuPts[i - 1].y;
          }
          if (i < n - 1) {
            dx += enuPts[i + 1].x - enuPts[i].x;
            dy += enuPts[i + 1].y - enuPts[i].y;
          }

          let len = Math.hypot(dx, dy);
          if (len === 0 && i < n - 1) {
            dx = enuPts[i + 1].x - enuPts[i].x;
            dy = enuPts[i + 1].y - enuPts[i].y;
            len = Math.hypot(dx, dy);
          }
          if (len === 0 && i > 0) {
            dx = enuPts[i].x - enuPts[i - 1].x;
            dy = enuPts[i].y - enuPts[i - 1].y;
            len = Math.hypot(dx, dy);
          }

          if (len === 0) {
            out[i] = new Cartesian3(enuPts[i].x, enuPts[i].y, enuPts[i].z);
            continue;
          }

          const ux = dx / len,
            uy = dy / len;
          const leftX = -uy,
            leftY = ux;
          const ox = enuPts[i].x + leftX * signedMeters;
          const oy = enuPts[i].y + leftY * signedMeters;
          out[i] = new Cartesian3(ox, oy, enuPts[i].z);
        }

        return out.map((p) => Matrix4.multiplyByPoint(enuToEcef, p, new Cartesian3()));
      };

      const srcPropsToPlain = (entity) => {
        try {
          const v = entity?.properties?.getValue?.(JulianDate.now()) || {};
          return v && typeof v === "object" ? { ...v } : {};
        } catch (_) {
          return {};
        }
      };

      const signed = geojson.direction === "left" ? geojson.distanceMeters : -geojson.distanceMeters;

      for (const src of selected) {
        const positions = getPositions(src);
        if (!positions || positions.length < 2) continue;

        const style = cloneStyle(src.polyline);
        const offsetPositions = offsetPolylineENU(positions, signed);

        const base = srcPropsToPlain(src);
        const merged = { ...base };

        for (const row of properties) {
          const k = row?.key;
          if (!k || String(k).trim() === "") continue;
          if (isExactIdKey(k)) continue;
          merged[k] = row.value;
        }

        merged.id = allocateNextIdForLayer(targetLayer);
        merged.creator = getUsernameAndUserIdFormat(userData);

        const newEntityOptions = {
          name: src.name ? `${src.name} (offset ${geojson.distanceMeters}m ${geojson.direction})` : "parallel",
          properties: new PropertyBag(merged),
          geojsonType: Type.ASSET.GEOJSON,
          polyline: {
            positions: offsetPositions,
            width: Math.max(1, (style.width ?? 2) - 3),
            material: style.material,
            clampToGround: style.clampToGround,
            arcType: style.arcType,
            zIndex: style.zIndex,
          },
        };

        const newEntity = targetLayer.dataSource.entities.add(newEntityOptions);

        applyTemplateStyle(newEntity, templateEntity);

        pushUndo({
          type: "add",
          entitySnapshot: cloneEntity(newEntity),
          entityId: newEntity.id,
        });
      }

      setMapObjects((prev) => prev.map((o) => (o.id === targetLayer.id ? { ...o } : o)));
      setOpen(false);
    } else {
      geojson.properties = propsObj;

      GeoJsonDataSource.load(geojson)
        .then((tempDataSource) => {
          const entities = tempDataSource.entities.values;

          const doAutoCut = isLaneMarkerOrVirtualLine(currentLayerEditing);
          const cutLineEntities = doAutoCut ? collectCutLineEntities(mapObjects) : [];

          for (let entity of entities) {
            const newEntityOptions = {
              name: entity.name || "geojson feature",
              properties: entity.properties,
              geojsonType: Type.ASSET.GEOJSON,
            };

            if (entity.position?.getValue) {
              newEntityOptions.position = entity.position.getValue(JulianDate.now());
              newEntityOptions.point = {
                pixelSize: 13,
                color: Color.RED,
                outlineColor: Color.RED,
                outlineWidth: 1,
                disableDepthTestDistance: Number.POSITIVE_INFINITY,
              };
            }

            if (entity.polyline) newEntityOptions.polyline = entity.polyline;
            if (entity.polygon) newEntityOptions.polygon = entity.polygon;

            const newEntity = targetLayer.dataSource.entities.add(newEntityOptions);
            const newEntityProps = newEntity.properties?.getValue?.(JulianDate.now()) || {};
            newEntity.properties = new PropertyBag({
              ...(newEntityProps && typeof newEntityProps === "object" ? newEntityProps : {}),
              id: allocateNextIdForLayer(targetLayer),
            });
            applyTemplateStyle(newEntity, templateEntity);

            const canTryCut = doAutoCut && !!newEntity.polyline;
            if (canTryCut) {
              const worldPositions = getPolylinePositions(newEntity);
              const maxAng = maxTurnAngleDeg(worldPositions);

              if (maxAng <= 10) {
                const segments = splitPolylineByCutLines(worldPositions, cutLineEntities);

                if (Array.isArray(segments) && segments.length >= 2) {
                  const originalSnapshot = cloneEntity(newEntity);
                  targetLayer.dataSource.entities.remove(newEntity);

                  const now = JulianDate.now();
                  const baseProps = newEntityOptions?.properties?.getValue?.(now) || {};
                  const mergedBase = { ...(baseProps && typeof baseProps === "object" ? baseProps : {}) };

                  // Apply popup props to all segments (excluding exact "id")
                  for (const k of Object.keys(propsObj || {})) {
                    if (!isExactIdKey(k)) mergedBase[k] = propsObj[k];
                  }

                  // Keep original polyline style
                  const poly = newEntityOptions.polyline || {};
                  const width = typeof poly.width?.getValue === "function" ? poly.width.getValue(now) : (poly.width ?? 2.0);
                  const material = poly.material ?? Color.YELLOW;
                  const clampToGround = typeof poly.clampToGround?.getValue === "function" ? !!poly.clampToGround.getValue(now) : !!poly.clampToGround;
                  const arcType = poly.arcType;
                  const zIndex = typeof poly.zIndex?.getValue === "function" ? poly.zIndex.getValue(now) : (poly.zIndex ?? 0);

                  const newIds = [];
                  for (const segPositions of segments) {
                    const merged = { ...mergedBase, id: allocateNextIdForLayer(targetLayer) };

                    const segEntity = targetLayer.dataSource.entities.add({
                      name: newEntityOptions.name,
                      properties: new PropertyBag(merged),
                      geojsonType: Type.ASSET.GEOJSON,
                      polyline: {
                        positions: segPositions,
                        width,
                        material,
                        clampToGround,
                        arcType,
                        zIndex,
                      },
                    });

                    applyTemplateStyle(segEntity, templateEntity);
                    newIds.push(segEntity.id);
                  }

                  // One undo should revert the whole split
                  pushUndo({
                    type: "split",
                    originalEntity: originalSnapshot,
                    newEntities: newIds,
                    dsRef: targetLayer.dataSource,
                  });

                  continue;
                }
              }
            }

            // Default behavior (unchanged)
            pushUndo({
              type: "add",
              entitySnapshot: cloneEntity(newEntity),
              entityId: newEntity.id,
              dsRef: targetLayer.dataSource,
            });
          }

          setMapObjects((prev) => prev.map((o) => (o.id === targetLayer.id ? { ...o } : o)));
          setOpen(false);
        })
        .catch((error) => {
          console.error("Error loading GeoJSON:", error);
        });
    }

    // Save last entered props (excluding exact "id")
    const layerKey = getLayerKey(currentLayerEditing);
    setSavedPropsInNewEntityLayerKey(layerKey);

    const columnKeys = getColumnKeys(currentLayerEditing);
    const allowed = columnKeys.length ? new Set(columnKeys) : null;

    setSavedPropsInNewEntity((prev) => {
      const next = { ...prev };
      for (const row of properties) {
        const k = row?.key;
        if (!k || isExactIdKey(k)) continue;

        if (allowed && !allowed.has(k)) continue;
        next[k] = row.value;
      }
      return next;
    });
  };

  return (
    <Dialog.Root placement={"top"} motionPreset="slide-in-top" open={open} onOpenChange={(e) => setOpen(e.open)}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Properties</Dialog.Title>
            </Dialog.Header>

            <Dialog.Body>
              <VStack alignItems="start" gap={1}>
                <select
                  value={targetLayerId}
                  onChange={(e) => setTargetLayerId(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: "6px",
                    marginBottom: "5px",
                  }}
                >
                  <option value="">Select layer</option>
                  {targetOptions.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.file_name}
                    </option>
                  ))}
                </select>

                {targetLayer &&
                  properties.map((item) => (
                    <HStack gap={2} mb={1} key={item.index} width="full">
                      <Input
                        placeholder="Key"
                        value={item.key}
                        disabled={["creator", "modifier", "att_editor"].includes(item.key) ? true : false}
                        onChange={(v) => {
                          const nextKey = v.target.value;
                          setProperties((prev) => prev.map((row) => (row.index === item.index ? { ...row, key: nextKey } : row)));
                        }}
                      />

                      <>
                        <Input
                          placeholder="Value"
                          value={item.value}
                          list={`suggestions-${item.index}`}
                          disabled={["creator", "modifier", "att_editor"].includes(item.key) ? true : false}
                          onChange={(v) => {
                            const rawValue = v.target.value;
                            const inputType = v?.nativeEvent?.inputType || "";
                            const isDeletion = typeof inputType === "string" && inputType.startsWith("delete");
                            const nextValue = normalizeShorthandValueForKey(item.key, rawValue, isDeletion);

                            setProperties((prev) => prev.map((row) => (row.index === item.index ? { ...row, value: nextValue } : row)));
                          }}
                        />
                        {Array.isArray(item.data) && item.data.length > 0 && (
                          <datalist id={`suggestions-${item.index}`}>
                            {item.data.map((val, i) => (
                              <option key={i} value={val} />
                            ))}
                          </datalist>
                        )}
                      </>

                      <Button
                        colorPalette={"red"}
                        onClick={() => {
                          setProperties((prev) => prev.filter((row) => row.index !== item.index));
                        }}
                      >
                        <LuMinus />
                      </Button>
                    </HStack>
                  ))}
              </VStack>
            </Dialog.Body>

            <Dialog.Footer>
              <Button
                disabled={!targetLayer}
                colorPalette={"black"}
                onClick={() => {
                  setProperties((prev) => [...prev, { index: createUniqueId(), key: "", value: "", data: [] }]);
                }}
              >
                <LuPlus />
              </Button>

              <Dialog.ActionTrigger asChild>
                <Button
                  variant="outline"
                  onClick={() => {
                    setCursorMode(Type.CURSOR.DEFAULT);
                  }}
                >
                  Cancel
                </Button>
              </Dialog.ActionTrigger>

              <Button onClick={add} disabled={!targetLayer}>
                Save
              </Button>
            </Dialog.Footer>

            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default LayersEditPropertiesPopup;
